import React, { memo } from 'react';
import { GiftIcon, UtensilsCrossedIcon, AlarmClockIcon, CalendarCheckIcon, ClipboardXIcon, RepeatIcon, CheckCircle2Icon, AlertTriangleIcon, XCircleIcon, PrinterIcon, ListChecksIcon, PenLineIcon, CalendarDaysIcon, BoxIcon } from 'lucide-react';
import { FadeIn } from '../ui/FadeIn';
import { CTAButton } from '../ui/CTAButton';
type Bonus = {
  number: string;
  title: string;
  value: string;
  Icon: BoxIcon;
  image: string;
  imageAlt: string;
  intro: string;
  body: React.ReactNode;
};
const bonuses: Bonus[] = [{
  number: 'BONO #1',
  title: 'El Decodificador de Cenas Seguras',
  value: '$47',
  Icon: UtensilsCrossedIcon,
  image: 'https://cdn.magicpatterns.com/uploads/36UqwhQ4SuiqDuUXCPDGTA/bn1_.png',
  imageAlt: 'Portada del libro El Decodificador de Cenas Seguras',
  intro: 'El mayor sabotaje nocturno no ocurre mientras duermes. Ocurre en los 60 minutos antes.',
  body: <>
        <p data-id="element-12">
          Hay alimentos que parecen completamente inofensivos a las 7 PM y que a
          las 2 AM se convierten en la razón exacta por la que el ardor aparece.
        </p>
        <p data-id="element-13">
          No es tu culpa no saberlo. Nadie te explicó la diferencia entre lo que
          es saludable en general y lo que es seguro mecánicamente para tu
          esófago en las horas previas al sueño.
        </p>
        <p data-id="element-14">
          El <strong data-id="element-15">Decodificador de Cenas Seguras</strong> es una guía visual
          de decisión rápida que te permite saber, en menos de 30 segundos, si
          lo que estás a punto de cenar va a trabajar a tu favor o en tu contra
          esa noche.
        </p>
        <p data-id="element-16">
          No es una dieta. No es una lista de restricciones. Es un sistema de
          clasificación inteligente.
        </p>
        <p className="font-medium text-slate-200" data-id="element-17">Con él sabrás exactamente:</p>
        <ul className="space-y-2 mt-2" data-id="element-18">
          <li className="flex gap-3" data-id="element-19">
            <CheckCircle2Icon size={18} className="text-green-500 flex-shrink-0 mt-1" data-id="element-20" />
            <span data-id="element-21">
              Qué alimentos crean las condiciones correctas para que el
              protocolo funcione
            </span>
          </li>
          <li className="flex gap-3" data-id="element-22">
            <AlertTriangleIcon size={18} className="text-amber-500 flex-shrink-0 mt-1" data-id="element-23" />
            <span data-id="element-24">Cuáles son neutros y en qué cantidad</span>
          </li>
          <li className="flex gap-3" data-id="element-25">
            <XCircleIcon size={18} className="text-red-500 flex-shrink-0 mt-1" data-id="element-26" />
            <span data-id="element-27">
              Cuáles aumentan la presión abdominal y la probabilidad de reflujo
              nocturno aunque parezcan inocentes
            </span>
          </li>
        </ul>
        <p className="text-amber-400 font-medium italic mt-4" data-id="element-28">
          Una sola decisión mejor en la cena puede cambiar completamente lo que
          ocurre a las 2 AM.
        </p>
      </>
}, {
  number: 'BONO #2',
  title: 'La Tarjeta Anti-Pánico de las 2 AM',
  value: '$27',
  Icon: AlarmClockIcon,
  image: 'https://cdn.magicpatterns.com/uploads/7TxCSq2CfHLFsWqEa6easz/bn2_.png',
  imageAlt: 'Portada del libro La Tarjeta Anti-Pánico de las 2 AM',
  intro: 'Porque hay noches en que el ardor llega de todas formas.',
  body: <>
        <p data-id="element-29">
          Y en ese momento, a las 2 de la madrugada, con el cerebro a medio
          despertar y el pecho ardiendo, no quieres buscar en el protocolo
          completo qué hacer.
        </p>
        <p data-id="element-30">Quieres una respuesta inmediata. Clara. Sin tener que pensar.</p>
        <p data-id="element-31">
          La <strong data-id="element-32">Tarjeta Anti-Pánico de las 2 AM</strong> es exactamente
          eso.
        </p>
        <p data-id="element-33">
          Un recurso de respuesta inmediata diseñado para ese momento
          específico. Una secuencia de pasos ordenados que puedes seguir en los
          primeros 90 segundos después de despertar con ardor, sin levantarte de
          la cama si no es necesario, sin tomar nada que interrumpa el
          protocolo.
        </p>
        <p data-id="element-34">
          Está diseñada para imprimirse o guardarse en el teléfono como pantalla
          de inicio.
        </p>
        <p className="text-amber-400 font-medium italic mt-4" data-id="element-35">
          Porque cuando el ardor llega en la madrugada, no tienes tiempo de
          recordar. Necesitas seguir una secuencia. Y esta tarjeta te la da.
        </p>
      </>
}, {
  number: 'BONO #3',
  title: 'El Plan de Adopción en 7 Noches',
  value: '$67',
  Icon: CalendarCheckIcon,
  image: 'https://cdn.magicpatterns.com/uploads/6n1QTxd1XEsDVaniosMsob/bn3_.png',
  imageAlt: 'Portada del libro El Plan de Adopción en 7 Noches',
  intro: 'El protocolo funciona. Pero solo si lo implementas correctamente desde el inicio.',
  body: <>
        <p data-id="element-36">Los primeros siete días son críticos.</p>
        <p data-id="element-37">
          No porque el sistema sea complicado, sino porque cualquier cambio de
          hábito nocturno tiene una curva de adaptación. Y sin una guía de
          implementación progresiva, la mayoría de las personas empieza bien
          pero afloja en la tercera o cuarta noche cuando los resultados no son
          perfectos todavía.
        </p>
        <p data-id="element-38">
          El <strong data-id="element-39">Plan de Adopción en 7 Noches</strong> es tu hoja de ruta de
          implementación completa. Noche a noche. Paso a paso.
        </p>
        <p className="font-medium text-slate-200" data-id="element-40">
          Cada día del plan está diseñado para:
        </p>
        <ul className="space-y-2 mt-2" data-id="element-41">
          {['Introducir el protocolo de forma progresiva y sostenible', 'Reducir la fricción de los primeros días cuando el hábito todavía no es automático', 'Darte un registro claro de lo que funciona en tu caso específico', 'Construir la consistencia correcta antes de que el protocolo se vuelva automático'].map((item, i) => <li key={i} className="flex gap-3" data-id="element-42">
              <CheckCircle2Icon size={18} className="text-green-500 flex-shrink-0 mt-1" data-id="element-43" />
              <span data-id="element-44">{item}</span>
            </li>)}
        </ul>
        <p className="text-amber-400 font-medium italic mt-4" data-id="element-45">
          No tienes que adivinar qué hacer la primera noche. Ni la segunda. Ni
          la séptima. El plan ya está hecho. Solo sigues la secuencia.
        </p>
      </>
}, {
  number: 'BONO #4',
  title: 'El Cheat Sheet de Errores Nocturnos',
  value: '$37',
  Icon: ClipboardXIcon,
  image: 'https://cdn.magicpatterns.com/uploads/jvygZG2V8sShbfaM4hFWMX/bn4_.png',
  imageAlt: 'Portada del libro El Cheat Sheet de Errores Nocturnos',
  intro: 'Hay errores que destruyen el protocolo sin que te des cuenta.',
  body: <>
        <p data-id="element-46">
          No son errores obvios. Son micro-decisiones que parecen neutrales y
          que en realidad crean las condiciones exactas para que el reflujo
          aparezca esa noche.
        </p>
        <p data-id="element-47">
          Después de años estudiando los patrones de personas con reflujo
          nocturno, identifiqué los <strong data-id="element-48">11 errores más comunes</strong> que
          cometen incluso quienes ya están intentando hacer las cosas bien.
        </p>
        <p data-id="element-49">
          El <strong data-id="element-50">Cheat Sheet de Errores Nocturnos</strong> es una referencia
          rápida que te permite:
        </p>
        <ul className="space-y-2 mt-2" data-id="element-51">
          {['Identificar en segundos si estás cometiendo alguno de estos errores esa noche', 'Corregir el rumbo antes de acostarte', 'Entender exactamente por qué ciertas noches funcionan y otras no'].map((item, i) => <li key={i} className="flex gap-3" data-id="element-52">
              <CheckCircle2Icon size={18} className="text-green-500 flex-shrink-0 mt-1" data-id="element-53" />
              <span data-id="element-54">{item}</span>
            </li>)}
        </ul>
        <p className="text-amber-400 font-medium italic mt-4" data-id="element-55">
          Porque a veces la diferencia entre una noche de descanso y una noche
          de ardor es un solo error que ni siquiera sabías que estabas
          cometiendo.
        </p>
      </>
}, {
  number: 'BONO #5',
  title: 'El Guión de Consistencia Sin Fuerza de Voluntad',
  value: '$57',
  Icon: RepeatIcon,
  image: 'https://cdn.magicpatterns.com/uploads/1zGTDxAvk1GtXLSmTsrdLu/bn_5.png',
  imageAlt: 'Portada del libro El Guión de Consistencia Sin Fuerza de Voluntad',
  intro: 'El mayor enemigo de un protocolo que funciona no es la duda. Es el cansancio de las 10 de la noche.',
  body: <>
        <p data-id="element-56">
          Cuando llegas al final del día agotado, con la guardia baja y el
          cerebro en modo automático, la mayoría de los hábitos se rompen.
        </p>
        <p data-id="element-57">
          No porque no quieras mantenerlos. Sino porque dependen de memoria,
          motivación y decisión activa en el peor momento posible para tomar
          decisiones.
        </p>
        <p data-id="element-58">
          El <strong data-id="element-59">Guión de Consistencia Sin Fuerza de Voluntad</strong>{' '}
          resuelve exactamente ese problema.
        </p>
        <p data-id="element-60">
          Es un sistema de anclajes de hábito diseñado para que el protocolo
          ocurra casi solo, sin que tengas que recordar cada paso, sin depender
          de motivación alta y sin necesitar disciplina heroica cada noche.
        </p>
        <p className="font-medium text-slate-200" data-id="element-61">Incluye:</p>
        <ul className="space-y-2 mt-2" data-id="element-62">
          <li className="flex gap-3" data-id="element-63">
            <ListChecksIcon size={18} className="text-amber-500 flex-shrink-0 mt-1" data-id="element-64" />
            <span data-id="element-65">
              Los 4 anclajes de hábito que hacen que la rutina se vuelva
              automática
            </span>
          </li>
          <li className="flex gap-3" data-id="element-66">
            <AlarmClockIcon size={18} className="text-amber-500 flex-shrink-0 mt-1" data-id="element-67" />
            <span data-id="element-68">
              Una rutina express de 60 segundos para seguir antes de dormir
            </span>
          </li>
          <li className="flex gap-3" data-id="element-69">
            <PenLineIcon size={18} className="text-amber-500 flex-shrink-0 mt-1" data-id="element-70" />
            <span data-id="element-71">Plantillas para personalizar tu secuencia nocturna</span>
          </li>
          <li className="flex gap-3" data-id="element-72">
            <CalendarDaysIcon size={18} className="text-amber-500 flex-shrink-0 mt-1" data-id="element-73" />
            <span data-id="element-74">Tracker de repetición de 7 días</span>
          </li>
          <li className="flex gap-3" data-id="element-75">
            <PrinterIcon size={18} className="text-amber-500 flex-shrink-0 mt-1" data-id="element-76" />
            <span data-id="element-77">Rutina imprimible lista para pegar en tu cuarto</span>
          </li>
        </ul>
        <p className="text-amber-400 font-medium italic mt-4" data-id="element-78">
          Porque la consistencia no nace de la fuerza de voluntad. Nace de una
          secuencia tan simple que no requiere pensarla.
        </p>
      </>
}];
const summaryRows = [{
  label: 'El Mapa de la Noche Sin Reflujo: El Protocolo 3-2-1',
  value: '$97',
  highlight: true
}, {
  label: 'Bono #1 — El Decodificador de Cenas Seguras',
  value: '$47'
}, {
  label: 'Bono #2 — La Tarjeta Anti-Pánico de las 2 AM',
  value: '$27'
}, {
  label: 'Bono #3 — El Plan de Adopción en 7 Noches',
  value: '$67'
}, {
  label: 'Bono #4 — El Cheat Sheet de Errores Nocturnos',
  value: '$37'
}, {
  label: 'Bono #5 — El Guión de Consistencia Sin Fuerza de Voluntad',
  value: '$57'
}];
export function BonusSection() {
  return <section className="py-12 space-y-12" data-id="element-79">
      {/* Opening Block */}
      <FadeIn data-id="element-80">
        <div className="text-center" data-id="element-81">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-amber-500/10 border border-amber-500/30 mb-6" data-id="element-82">
            <GiftIcon size={32} className="text-amber-500" data-id="element-83" />
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6" data-id="element-84">
            Pero eso no es todo.
          </h2>
          <div className="space-y-4 text-lg text-slate-300 leading-relaxed text-left max-w-2xl mx-auto" data-id="element-85">
            <p data-id="element-86">
              Cuando tomé la decisión de lanzar este protocolo, quise asegurarme
              de que nadie que lo adquiriera se quedara a mitad del camino.
            </p>
            <p data-id="element-87">
              Porque conozco exactamente qué pasa cuando alguien compra una
              solución y no la implementa.
            </p>
            <p data-id="element-88">
              No es falta de motivación. No es falta de inteligencia. Es falta
              de herramientas para los momentos exactos en que el sistema falla.
            </p>
            <p data-id="element-89">
              Así que además del Protocolo 3-2-1 completo, decidí incluir{' '}
              <strong className="text-white" data-id="element-90">cinco recursos</strong> que
              normalmente entregaría por separado, y que juntos representan{' '}
              <strong className="text-amber-400" data-id="element-91">
                más de $300 en valor real
              </strong>
              .
            </p>
            <p className="text-amber-400 font-medium" data-id="element-92">
              Hoy los recibes sin costo adicional. Los cinco. Todos. Desde el
              primer día.
            </p>
          </div>
        </div>
      </FadeIn>

      {/* Bonus Cards */}
      {bonuses.map((bonus, i) => {
      const Icon = bonus.Icon;
      return <FadeIn key={i} data-id="element-93">
            <article className="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden shadow-xl" data-id="element-94">
              {/* Image */}
              <div className="bg-slate-950 p-6 sm:p-8 border-b border-slate-800 flex items-center justify-center" data-id="element-95">
                <img src={bonus.image} alt={bonus.imageAlt} className="w-full max-w-xs h-auto object-contain drop-shadow-2xl" data-id="element-96" />
              </div>

              {/* Content */}
              <div className="p-6 sm:p-8" data-id="element-97">
                {/* Header */}
                <div className="flex items-start gap-4 mb-5 pb-5 border-b border-slate-800" data-id="element-98">
                  <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-amber-500/10 border border-amber-500/30 flex items-center justify-center" data-id="element-99">
                    <Icon size={22} className="text-amber-500" data-id="element-100" />
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-101">
                    <div className="text-xs font-bold tracking-widest text-amber-500 uppercase mb-1" data-id="element-102">
                      {bonus.number}
                    </div>
                    <h3 className="text-xl sm:text-2xl font-bold text-white leading-tight" data-id="element-103">
                      {bonus.title}
                    </h3>
                  </div>
                </div>

                {/* Value tag */}
                <div className="flex flex-wrap items-center gap-3 mb-6" data-id="element-104">
                  <span className="text-sm text-slate-400" data-id="element-105">
                    Valor real:{' '}
                    <span className="text-slate-300 line-through" data-id="element-106">
                      {bonus.value}
                    </span>
                  </span>
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold bg-green-900/30 text-green-400 border border-green-800/50" data-id="element-107">
                    HOY: GRATIS
                  </span>
                </div>

                {/* Intro */}
                <p className="text-lg font-medium text-white mb-4 leading-relaxed" data-id="element-108">
                  {bonus.intro}
                </p>

                {/* Body */}
                <div className="space-y-4 text-slate-300 leading-relaxed" data-id="element-109">
                  {bonus.body}
                </div>
              </div>
            </article>
          </FadeIn>;
    })}

      {/* Total Value Summary */}
      <FadeIn data-id="element-110">
        <div className="bg-slate-900 border-2 border-amber-500/30 rounded-2xl overflow-hidden shadow-2xl" data-id="element-111">
          <div className="bg-amber-500/10 border-b border-amber-500/30 p-6 text-center" data-id="element-112">
            <div className="inline-flex items-center gap-2 text-amber-500 font-bold uppercase tracking-widest text-sm mb-2" data-id="element-113">
              <GiftIcon size={18} data-id="element-114" />
              <span data-id="element-115">Todo lo que recibes hoy</span>
            </div>
            <h3 className="text-2xl sm:text-3xl font-bold text-white" data-id="element-116">
              Resumen de Valor Total
            </h3>
          </div>

          <div className="divide-y divide-slate-800" data-id="element-117">
            {summaryRows.map((row, i) => <div key={i} className={`flex items-center justify-between gap-4 p-4 sm:p-5 ${row.highlight ? 'bg-slate-800/30' : ''}`} data-id="element-118">
                <div className="flex items-start gap-3 flex-1 min-w-0" data-id="element-119">
                  {row.highlight ? <CheckCircle2Icon size={20} className="text-green-500 flex-shrink-0 mt-0.5" data-id="element-120" /> : <GiftIcon size={20} className="text-amber-500 flex-shrink-0 mt-0.5" data-id="element-121" />}
                  <span className={`text-sm sm:text-base ${row.highlight ? 'text-white font-medium' : 'text-slate-300'}`} data-id="element-122">
                    {row.label}
                  </span>
                </div>
                <span className="text-slate-400 font-medium whitespace-nowrap text-sm sm:text-base" data-id="element-123">
                  {row.value}
                </span>
              </div>)}
          </div>

          <div className="bg-slate-950 p-6 sm:p-8 border-t-2 border-amber-500/30 text-center" data-id="element-124">
            <div className="text-sm text-slate-400 uppercase tracking-widest mb-2" data-id="element-125">
              Valor Total Real
            </div>
            <div className="text-5xl sm:text-6xl font-black text-amber-500" data-id="element-126">
              $332
            </div>
          </div>
        </div>
      </FadeIn>

      {/* Closing Block */}
      <FadeIn data-id="element-127">
        <div className="text-center space-y-6 max-w-2xl mx-auto" data-id="element-128">
          <p className="text-xl text-white font-medium" data-id="element-129">
            Todo eso es tuyo hoy.
          </p>
          <div className="space-y-2 text-lg text-slate-300" data-id="element-130">
            <p data-id="element-131">
              No por <span className="line-through text-slate-500" data-id="element-132">$332</span>.
              No por <span className="line-through text-slate-500" data-id="element-133">$197</span>.
              No por <span className="line-through text-slate-500" data-id="element-134">$97</span>.
            </p>
            <p className="text-amber-400 font-medium" data-id="element-135">
              Por una fracción de lo que has gastado en antiácidos este año.
            </p>
          </div>
          <p className="text-slate-300 leading-relaxed" data-id="element-136">
            Y con algo que ningún antiácido te ha dado todavía: una forma de
            entender la mecánica real de lo que ocurre mientras duermes y una
            secuencia concreta para interrumpirla.
          </p>
        </div>
      </FadeIn>

      {/* CTA */}
      <FadeIn data-id="element-137">
        <div className="pt-4" data-id="element-138">
          <CTAButton text="✅ QUIERO EL PROTOCOLO + LOS 5 BONOS GRATIS →" data-id="element-139" />
          <div className="flex flex-wrap justify-center gap-4 sm:gap-6 text-sm text-slate-400 mt-5" data-id="element-140">
            <span className="flex items-center gap-1.5" data-id="element-141">
              <CheckCircle2Icon size={14} className="text-green-500" data-id="element-142" />
              Acceso inmediato
            </span>
            <span className="hidden sm:inline text-slate-700" data-id="element-143">•</span>
            <span className="flex items-center gap-1.5" data-id="element-144">
              <GiftIcon size={14} className="text-amber-500" data-id="element-145" />
              Todos los bonos incluidos
            </span>
            <span className="hidden sm:inline text-slate-700" data-id="element-146">•</span>
            <span className="flex items-center gap-1.5" data-id="element-147">
              <CheckCircle2Icon size={14} className="text-green-500" data-id="element-148" />
              Sin suscripciones
            </span>
          </div>
        </div>
      </FadeIn>
    </section>;
}